import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { FileBox, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import { DropZone } from '@/components/ui/drop-zone';
import ErrorMessage from '@/components/ErrorMessage';
import { IPostUploadData } from '@/redux/post/post.d';

function ModelUpload({
  formData,
  errors,
  setFormData,
}: {
  formData: IPostUploadData;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IPostUploadData>>;
}) {
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const modelFiles = event.target.files;
    if (modelFiles && modelFiles[0]) {
      const modelFileList = Array.from(modelFiles);

      const newModelList = modelFileList
        .filter(
          (newFile) =>
            !formData.models?.some(
              (existingModel) => existingModel.modelFile?.name === newFile.name
            )
        )
        .map((model) => ({
          id: Date.now().toString(),
          modelFile: model,
          modelURL: URL.createObjectURL(model),
        }));

      setFormData((prev) => {
        const existingToolData = prev.toolData || [];

        return {
          ...prev,
          models: [...(prev.models || []), ...newModelList],
          toolData: [...existingToolData],
        };
      });
    }
  };

  const handleRemoveFile = (file: File) => {
    setFormData((prev: IPostUploadData) => {
      const removedModel = prev.models.find(
        (model) => model.modelFile?.name === file.name
      );

      const updatedModels = prev.models.filter(
        (model) => model.modelFile?.name !== file.name
      );

      const updatedToolData = (prev.toolData || []).filter(
        (tool) => tool.modelId !== removedModel?.id
      );

      return {
        ...prev,
        models: updatedModels,
        toolData: updatedToolData,
      };
    });
  };

  return (
    <div className="space-y-6 font-tertiary">
      <div>
        <SectionHeader
          title="Upload your CNC models"
          description="Select relevant documents to complete your upload"
        />
        <DropZone onDrop={handleFileChange}>
          <Upload className="mx-auto h-12  w-12 text-muted-foreground" />
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Select a file or drag and drop here
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              GCODE, NGC, or NC files only
            </p>
          </div>
          <Input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept=".gcode,.ngc,.nc"
            id="file-upload"
            multiple
          />
          <Button
            variant="outline"
            className="mt-4 border-gradient border-2"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            Select file
          </Button>
        </DropZone>
        <ErrorMessage error={errors.models} />
      </div>

      <div className="flex flex-col gap-4 max-h-56 overflow-auto">
        {formData.models && formData.models.length > 0 && (
          <div className="space-y-4">
            {formData.models.map((model) => (
              <div
                className="flex items-center gap-2 py-[10px] px-[19px]"
                key={
                  model.modelFile
                    ? `${model.modelFile.name}-${model.modelFile.size}`
                    : 'undefined-key'
                }
              >
                <FileBox className="text-[#0F91D2]" />
                <div className="flex-1 truncate">
                  {model.modelFile?.name || 'Unknown File'}
                </div>
                <Button
                  variant="ghost"
                  className="rounded-full cursor-pointer"
                  onClick={() =>
                    model.modelFile && handleRemoveFile(model.modelFile)
                  }
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ModelUpload;
